// src/components/FAQ/FAQSection.jsx
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { faqCategories, faqData } from './faqData';

const FAQSection = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [openId, setOpenId] = useState(null);

    // Filter logic
    const filteredFAQs = useMemo(() => {
        return faqData.filter(faq => {
            const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
            const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [activeCategory, searchQuery]);

    const toggleQuestion = (id) => {
        setOpenId(openId === id ? null : id);
    };

    return (
        <section className="relative py-24 z-10">
            <div className="max-w-4xl mx-auto px-4">

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EFCD86]/10 border border-[#EFCD86]/20 text-[#EFCD86] text-sm font-semibold mb-6">
                        <HelpCircle className="w-4 h-4" />
                        <span>Got Questions?</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                        Frequently Asked <span className="text-[#EFCD86]">Questions</span>
                    </h2>
                    <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                        Everything you need to know about our services, process, and support.
                    </p>
                </motion.div>

                {/* Search Bar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="relative mb-8"
                >
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Search questions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-[#0d1f33]/80 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:border-[#EFCD86]/50 focus:outline-none transition-all duration-300"
                    />
                </motion.div>

                {/* Category Tabs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="flex flex-wrap gap-3 mb-10 justify-center"
                >
                    {faqCategories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${activeCategory === cat.id
                                    ? 'bg-[#EFCD86] text-[#0a1628] shadow-[0_0_20px_rgba(239,205,134,0.3)]'
                                    : 'bg-white/5 text-gray-400 border border-white/10 hover:border-[#EFCD86]/30 hover:text-white'
                                }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </motion.div>

                {/* FAQ Accordion */}
                <div className="space-y-3">
                    <AnimatePresence mode="popLayout">
                        {filteredFAQs.map((faq, index) => (
                            <motion.div
                                key={faq.id}
                                layout
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ delay: index * 0.05, duration: 0.4 }}
                                className={`rounded-2xl border transition-all duration-300 overflow-hidden ${openId === faq.id
                                        ? 'bg-[#0d1f33]/80 border-[#EFCD86]/30 shadow-[0_0_30px_rgba(239,205,134,0.1)]'
                                        : 'bg-[#0d1f33]/40 border-white/10 hover:border-white/20'
                                    }`}
                            >
                                {/* Question */}
                                <button
                                    onClick={() => toggleQuestion(faq.id)}
                                    className="w-full flex items-center justify-between p-6 text-left"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${openId === faq.id ? 'bg-[#EFCD86]/20 text-[#EFCD86]' : 'bg-white/5 text-gray-500'
                                            }`}>
                                            <span className="font-bold text-sm">0{faq.id}</span>
                                        </div>
                                        <span className={`text-lg font-medium transition-colors duration-300 ${openId === faq.id ? 'text-white' : 'text-gray-300'
                                            }`}>
                                            {faq.question}
                                        </span>
                                    </div>
                                    <motion.div
                                        animate={{ rotate: openId === faq.id ? 180 : 0 }}
                                        transition={{ duration: 0.3 }}
                                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${openId === faq.id ? 'bg-[#EFCD86] text-[#0a1628]' : 'bg-white/5 text-gray-500'
                                            }`}
                                    >
                                        <ChevronDown className="w-5 h-5" />
                                    </motion.div>
                                </button>

                                {/* Answer */}
                                <AnimatePresence>
                                    {openId === faq.id && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                                        >
                                            <div className="px-6 pb-6 pl-[72px]">
                                                <p className="text-gray-400 leading-relaxed">
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {/* No Results */}
                {filteredFAQs.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-12"
                    >
                        <p className="text-gray-500 text-lg">No questions found. Try different keywords.</p>
                    </motion.div>
                )}

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.7 }}
                    className="mt-16 text-center"
                >
                    <div className="inline-flex flex-col items-center gap-4 p-8 rounded-3xl bg-gradient-to-r from-[#EFCD86]/10 via-[#046A8C]/10 to-[#5BAA5A]/10 border border-white/10">
                        <MessageCircle className="w-12 h-12 text-[#EFCD86]" />
                        <h3 className="text-2xl font-bold text-white">Still have questions?</h3>
                        <p className="text-gray-400 max-w-md">
                            Can't find the answer you're looking for? Our team is here to help.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <a
                                href="/contact"
                                className="px-6 py-3 bg-[#EFCD86] text-[#0a1628] rounded-full font-semibold hover:bg-white transition-colors"
                            >
                                Contact Us
                            </a>
                            <a
                                href="/services"
                                className="px-6 py-3 border border-white/20 text-white rounded-full font-semibold hover:border-[#EFCD86] hover:text-[#EFCD86] transition-colors"
                            >
                                View Services
                            </a>
                        </div>
                    </div>
                </motion.div>

            </div>
        </section>
    );
};

export default FAQSection;